/**
 * Plain-text context from the certificate payload (metadata only — not file bodies).
 */

function lineForDoc(doc, i) {
  if (!doc || typeof doc !== 'object') return null;
  const title =
    typeof doc.title === 'string' && doc.title.trim()
      ? doc.title.trim()
      : typeof doc.documentType === 'string'
        ? doc.documentType.trim()
        : `Document ${i + 1}`;
  const cat =
    typeof doc.documentCategoryName === 'string' && doc.documentCategoryName.trim()
      ? ` [${doc.documentCategoryName.trim()}]`
      : '';
  const id = doc.documentId != null ? ` (id: ${doc.documentId})` : '';
  return `- ${title}${cat}${id}`;
}

export function buildDeviceChatContext(payload) {
  const md =
    payload && typeof payload.mainDevice === 'object' && payload.mainDevice
      ? payload.mainDevice
      : null;
  const parts = [];
  if (md) {
    if (md.productName) parts.push(`Product: ${md.productName}`);
    if (md.productCode) parts.push(`Product code: ${md.productCode}`);
    if (md.serialNumber) parts.push(`Serial: ${md.serialNumber}`);
  }
  const deviceSummary = parts.length ? parts.join('. ') + '.' : 'Device details from certificate.';

  const docs = Array.isArray(payload?.documents) ? payload.documents : [];
  const documentLines = docs.map((d, i) => lineForDoc(d, i)).filter(Boolean);

  const rawItemId = md?.itemId;
  const deviceItemId =
    rawItemId != null && rawItemId !== '' && !Number.isNaN(Number(rawItemId))
      ? Number(rawItemId)
      : null;

  const header = `[Device context — metadata only, not full document text]\n${deviceSummary}`;
  const docSection =
    documentLines.length > 0
      ? `\nDocuments linked to this device:\n${documentLines.join('\n')}`
      : '\nNo document list in this certificate payload.';

  return {
    deviceSummary,
    documentLines,
    deviceItemId,
    contextBlock: header + docSection,
  };
}

export function mockDeviceChatReply(userText, ctx) {
  const q = (userText || '').toLowerCase().trim();
  const titles = (ctx.documentLines || [])
    .map((line) => line.replace(/^-\s*/, ''))
    .filter(Boolean);

  if (!q) {
    return 'Ask a question about this device or its documents. (Demo: I only use names from this certificate.)';
  }

  if (/^(hi|hello|hey|good\s+(morning|afternoon|evening)|howdy)\b|^yo\b|^sup\b|thanks|thank you|thx\b|^ok\b|^okay\b|^cool\b|^nice\b/i.test(q)) {
    return `Hey — I’m here to help with **${ctx.deviceSummary.split('.')[0] || 'this device'}** and its documents (demo: I only see certificate metadata and document names). What would you like to know?`;
  }

  if (/what\s+doc|which\s+doc|list|documents?|manual|pdf|file/i.test(q)) {
    if (titles.length === 0) {
      return `${ctx.deviceSummary} There are no documents listed on this certificate yet.`;
    }
    return `From your certificate, these documents are listed:\n\n${titles.map((t) => `• ${t}`).join('\n')}\n\n(Demo: I do not yet read file contents — only this list.)`;
  }

  if (/serial|s\/n|sn\b/i.test(q) && /serial/i.test(ctx.deviceSummary)) {
    const m = ctx.deviceSummary.match(/Serial:\s*([^.]+)/i);
    if (m) return `The serial number on this certificate is **${m[1].trim()}**.`;
  }

  if (/model|product code|code\b/i.test(q)) {
    if (/Product code:/i.test(ctx.deviceSummary)) {
      const m = ctx.deviceSummary.match(/Product code:\s*([^.]+)/i);
      if (m) return `Product code: **${m[1].trim()}**.`;
    }
    if (/Product:/i.test(ctx.deviceSummary)) {
      const m = ctx.deviceSummary.match(/Product:\s*([^.]+)/i);
      if (m) return `Product: **${m[1].trim()}**.`;
    }
  }

  const words = q.split(/\s+/).filter((w) => w.length > 2);
  for (const t of titles) {
    const tl = t.toLowerCase();
    if (words.some((w) => tl.includes(w))) {
      return `Something related to **${t.split('(')[0].trim()}** appears in your document list. Full text search is not enabled in this demo — open the **Documents** tab to view downloads when available.`;
    }
  }

  return `I am running in **demo mode** and only see document **names** from this device’s certificate, not the files themselves.\n\nTry asking: “What documents are available?” or name a document type (e.g. manual).\n\n${ctx.deviceSummary}`;
}
